import { AxiosResponse } from "axios";
import { ReportStatesEnum } from "../constants/constants";
import { API_URL } from "../constants/url-config";
import { ReivewRequestModel } from "../models/review.model";
import {
    AssetClassModel,
    BaselClassificationModel,
    FrontierScenarioReportModel,
    RatingConfigurationModel,
    ScenarioFrontierModel,
    ScenarioFrontierUploadFileModel,
    VariableConfigurationModel
} from "../models/scenario-frontier.model";
import { deleteAsync, getAsync, postAsync, putAsync, uploadFileAsync } from "../utils/http-client";

const serviceUrl = API_URL.scenarioFrontierService;

const API_PATH = {
    report: "/report",
    configVariable: "/configuration/variable",
    configBaselClassification: "/configuration/basel-classification",
    configAssetClasses: "/configuration/asset-classes",
    configRating: "/configuration/rating",
    submitForApproval: "/report/submit-report",
    updateScenarioReport: "/report/scenario-report",
    updateScenarioReports: "/report/scenario-reports",
    assetGrowthReport: "/report/asset-growth-report",
    assetGrowthReports: "/report/asset-growth-reports",
    inputFile: "/report/input-file",
    includeConcentrationStressTest: "/report/include-concentration-stress-test",
    generateResult: "/report/generate-result",
    startOver: "/report/start-over",
    getApprovalReports: "approval-workflow/reports",
    approvalAction: "/approval-workflow/action",
    downloadExcel: "/report/download-excel",
    downloadSampleFile: "/report/download-sample-file"
}

const getReports = async (): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, API_PATH.report, undefined, false);
}

const getReportById = async (id: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.report}/${id}`);
}

const generateReport = async (item: ScenarioFrontierModel, showloading: boolean = true): Promise<AxiosResponse> => {
    const body = {
        fiscalYear: item.fiscalYear,
        quarter: item.quarter
    }
    return await postAsync(serviceUrl, API_PATH.report, body, undefined, showloading);
}

const updateScenarioReport = async (item: FrontierScenarioReportModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.updateScenarioReport, item);
}

const updateScenarioReports = async (items: FrontierScenarioReportModel[]): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.updateScenarioReports, items);
}

const assetGrowthReport = async (item: FrontierScenarioReportModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.assetGrowthReport, item);
}

const assetGrowthReports = async (items: FrontierScenarioReportModel[]): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.assetGrowthReports, items);
}

const uploadInputFile = async (model: ScenarioFrontierUploadFileModel): Promise<AxiosResponse> => {
    return await uploadFileAsync(serviceUrl, API_PATH.inputFile, model, undefined, false);
}

const deleteInputFile = async (id: number): Promise<AxiosResponse> => {
    return await deleteAsync(serviceUrl, `${API_PATH.inputFile}/${id}`, undefined, false);
}

const generateResult = async (id: number): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.generateResult}/${id}`, undefined, undefined, false);
}

const submitForApproval = async (id: number): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.submitForApproval}/${id}`);
}

const startOver = async (fiscalYear: number, quarter: number): Promise<AxiosResponse> => {
    const body = {
        fiscalYear,
        quarter
    };

    return await putAsync(serviceUrl, API_PATH.startOver, body);
}

const includeConcentrationStressTest = async (id: number, isInclude: boolean): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.includeConcentrationStressTest}/${id}?isInclude=${isInclude}`);
}

const getVariables = async (): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, API_PATH.configVariable, undefined, false);
}

const addVariable = async (item: VariableConfigurationModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, API_PATH.configVariable, item);
}

const updateVariable = async (item: VariableConfigurationModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.configVariable, item);
}

const deleteVariable = async (id: number): Promise<AxiosResponse> => {
    return await deleteAsync(serviceUrl, `${API_PATH.configVariable}/${id}`);
}

const getAssetClasses = async (): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, API_PATH.configAssetClasses, undefined, false);
}

const addAssetClasses = async (item: AssetClassModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, API_PATH.configAssetClasses, item);
}

const updateAssetClasses = async (item: AssetClassModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.configAssetClasses, item);
}

const deleteAssetClasses = async (id: number): Promise<AxiosResponse> => {
    return await deleteAsync(serviceUrl, `${API_PATH.configAssetClasses}/${id}`);
}

const getBaselClassifications = async (): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, API_PATH.configBaselClassification, undefined, false);

}

const updateBaselClassification = async (item: BaselClassificationModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.configBaselClassification, item);
}

const getRatings = async (): Promise<any> => {
    return await getAsync(serviceUrl, API_PATH.configRating, undefined, false);
}

const addRating = async (item: RatingConfigurationModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, API_PATH.configRating, item);
}

const updateRating = async (item: RatingConfigurationModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, API_PATH.configRating, item);
}

const deleteRating = async (id: number): Promise<AxiosResponse> => {
    return await deleteAsync(serviceUrl, `${API_PATH.configRating}/${id}`);
}


const getApprovalReports = async (state: ReportStatesEnum): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.getApprovalReports}?state=${state}`, undefined, false);
}

const approvalAction = async (model: ReivewRequestModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, API_PATH.approvalAction, model);
}

const downloadExcel = async (reportId: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.downloadExcel}/${reportId}`, undefined, true, undefined, undefined, true);
}

const downloadSampleFile = async (): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.downloadSampleFile}`, undefined, true, undefined, undefined, true);
}

export const ScenarioFrontierService = {
    getReports,
    submitForApproval,
    generateReport,
    getReportById,
    uploadInputFile,
    deleteInputFile,
    includeConcentrationStressTest,
    generateResult,
    startOver,

    updateScenarioReport,
    updateScenarioReports,

    assetGrowthReport,
    assetGrowthReports,

    getRatings,
    addRating,
    updateRating,
    deleteRating,

    getVariables,
    addVariable,
    updateVariable,
    deleteVariable,

    getAssetClasses,
    addAssetClasses,
    updateAssetClasses,
    deleteAssetClasses,

    getBaselClassifications,
    updateBaselClassification,

    getApprovalReports,
    approvalAction,

    downloadExcel,
    downloadSampleFile
};